import { useCallback, useEffect, useRef, type ReactNode } from 'react'

import { usePermission } from '@/shared/access'
import { parseCursorPage } from '@/shared/api/paging'
import { useApiClient } from '@/shared/api/use-api-client'
import { ActorNamesProvider, type ActorName, type ActorNameLookup } from '@/shared/details'

import { parseUserName, readUsersPermission, userNamesApi, userPath } from './user'

/** The most ids the API names in one call. */
const batchSize = 100

/**
 * Registers Site Management's name lookup for the `User` actor type, so every "Created by" line
 * names the person, from the display names of User Profiles on the viewer's site, linked to their
 * details: names come only for a viewer who may read people.
 */
export function UserNamesProvider(props: { readonly children: ReactNode }) {
  const client = useApiClient()
  // Kept current without changing the lookup, which would look every name up again: the client
  // changes when sign-in settles.
  const api = useRef(client)
  useEffect(() => {
    api.current = client
  }, [client])

  // Names come only for the people the viewer may read, so without the permission nothing is asked.
  const mayRead = usePermission(readUsersPermission)

  const fetchNames = useCallback(
    async (ids: readonly string[]) => {
      if (!mayRead) {
        return new Map<string, ActorName>()
      }
      const batches: (readonly string[])[] = []
      for (let start = 0; start < ids.length; start += batchSize) {
        batches.push(ids.slice(start, start + batchSize))
      }
      const pages = await Promise.all(
        batches.map(async (batch) => {
          const query = new URLSearchParams({ ids: batch.join(',') })
          return parseCursorPage(await api.current.get(`${userNamesApi}?${query.toString()}`), parseUserName)
        }),
      )
      return new Map<string, ActorName>(pages.flatMap((page) => page.data.map((name) => [name.id, { name: name.displayName, path: userPath(name.id) }] as const)))
    },
    [mayRead],
  )

  // Lookups asked in the same moment, as by every cell of a table, share one request.
  const pending = useRef<{ readonly ids: Set<string>; readonly names: Promise<ReadonlyMap<string, ActorName>> } | null>(null)
  const lookup = useCallback<ActorNameLookup>(
    async (ids) => {
      let batch = pending.current
      if (batch === null) {
        const asked = new Set<string>()
        const names = Promise.resolve().then(async () => {
          pending.current = null
          return await fetchNames([...asked])
        })
        batch = { ids: asked, names }
        pending.current = batch
      }
      for (const id of ids) {
        batch.ids.add(id)
      }
      return await batch.names
    },
    [fetchNames],
  )

  return (
    <ActorNamesProvider type="user" lookup={lookup}>
      {props.children}
    </ActorNamesProvider>
  )
}
